/**
 * Browser device detection for app-download flows.
 *
 * Pure store-link string building lives in `@butter/library/shared` (safe on the
 * server). These read `window.navigator`, so they are browser-only and live in
 * the UI `app` domain alongside the components that consume them.
 *
 * @module @butter/ui/domains/app/utils/device
 */

export type DeviceType = 'ios' | 'android' | 'desktop';

/**
 * True when the current device is iOS (iPhone / iPad / iPod).
 *
 * iPadOS 13+ Safari requests desktop sites by default and reports a Macintosh
 * user agent with no `ipad` token, so the UA alone misses modern iPads. Real
 * Macs report `maxTouchPoints === 0`, which disambiguates them.
 */
export const isIOSDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return true;
  return /macintosh/.test(ua) && window.navigator.maxTouchPoints > 1;
};

/** True when the current device is Android. */
export const isAndroidDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /android/.test(window.navigator.userAgent.toLowerCase());
};

/**
 * Coarse device bucket. Desktop / SSR resolves to `'desktop'`; callers that need
 * a single store link typically fall back to iOS there by convention.
 */
export const getDeviceType = (): DeviceType => {
  if (isIOSDevice()) return 'ios';
  if (isAndroidDevice()) return 'android';
  return 'desktop';
};
